import { useParams } from 'react-router-dom';
import type { DefaultError } from '@tanstack/query-core';
import { UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { fetchCampaign } from 'api/campaigns';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { AccountSetting, Campaign } from 'types/domain';

interface TransformedCampaign extends Campaign {
    hideFees?: AccountSetting;
    disableTips?: AccountSetting;
    requireFees?: AccountSetting;
    forceEndDate?: AccountSetting;
    hideMoneyRaised?: AccountSetting;
    hideSupporterFeed?: AccountSetting;
    hideContactInformation?: AccountSetting;
    disableSpaces?: AccountSetting;
    donationButtonText?: AccountSetting;
    hideOtherAmountField?: AccountSetting;
    thanksMessage?: AccountSetting;
    minimumDonationRequired?: AccountSetting;
    metaTitle?: AccountSetting;
    metaDescription?: AccountSetting;
    themeColor?: AccountSetting;
    maintainAspectRatio?: AccountSetting;
    customDonationSettings?: AccountSetting;
    minimumDonationSettings?: AccountSetting;
    defaultPaymentFrequencySettings?: AccountSetting;
    enabledPaymentFrequenciesSettings?: AccountSetting;
    enableRecurringPayments?: AccountSetting;
    fundraiseButtonText?: AccountSetting;
    disableTeamFundraising?: AccountSetting;
    disableFundraiseButton?: AccountSetting;
    disableCreatingNewTeams?: AccountSetting;
    hideMemberRaised?: AccountSetting;
    enableRegistrationFundraising?: AccountSetting;
    registrationThankYouMessage?: AccountSetting;
    enableMultiTeam?: AccountSetting;
    multiTeamLimit?: AccountSetting;
    hideTeamRaised?: AccountSetting;
    hideFund?: AccountSetting;
    defaultFund?: AccountSetting;
    customDonations?: AccountSetting;
    disablePayments?: AccountSetting;
    requirePhoneNumbers?: AccountSetting;
    requireBillingAddress?: AccountSetting;
    defaultPaymentFrequency?: AccountSetting;
    disableRecurringPayments?: AccountSetting;
    checkPaymentInstructions?: AccountSetting;
    enabledPaymentFrequencies?: AccountSetting;
    disablePaymentOnBehalfOfCompany?: AccountSetting;
}

interface Options
    extends Omit<
        UseSuspenseQueryOptions<TransformedCampaign, DefaultError, TransformedCampaign, any>,
        'queryKey'
    > {
    campaignId?: number;
}

type PossibleCampaignOptions = Omit<
    UseSuspenseQueryOptions<
        TransformedCampaign | null,
        DefaultError,
        TransformedCampaign | null,
        any
    >,
    'queryKey'
> & {
    campaignId?: number;
};

const useCurrentPossibleCampaign = (options: PossibleCampaignOptions = {}) => {
    const { campaignId: id, retry = false, select, ...rest } = options;
    const { settings: accountSettings } = useCurrentAccount();
    const { campaign_id } = useParams<{ campaign_id: string }>();
    const campaignId = id ?? Number(campaign_id);

    return useSuspenseQuery({
        queryKey: campaignQueryKeys.campaign(campaignId),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchCampaign({ campaignId: campaignId }),
                !isNaN(campaignId),
            ),
        select: campaign =>
            campaign
                ? appendSettingsToCampaign(select?.(campaign) ?? campaign, accountSettings)
                : null,
        retry,
        ...rest,
    });
};

const useCurrentCampaign = (options: Options = {}) => {
    const { campaignId: id, retry = false, select, ...rest } = options;
    const { settings: accountSettings } = useCurrentAccount();
    const { campaign_id } = useParams<{ campaign_id: string }>();
    const campaignId = !!id ? id : Number(campaign_id);

    return useSuspenseQuery({
        queryKey: campaignQueryKeys.campaign(campaignId),
        queryFn: () => fetchCampaign({ campaignId }),
        select: campaign =>
            appendSettingsToCampaign(select?.(campaign) ?? campaign, accountSettings),
        retry,
        ...rest,
    });
};

const appendSettingsToCampaign = (
    campaign: Campaign,
    accountSettings: AccountSetting[],
): TransformedCampaign => {
    const settings = [...campaign.settings, ...accountSettings];
    const getSettingByCodeAndName = (code: number, name: string) =>
        settings.find(item => item.code === code && item.name === name);

    return {
        ...campaign,
        hideFees: getSettingByCodeAndName(Settings.HideFees.Code, Settings.HideFees.Name),
        disableTips: getSettingByCodeAndName(Settings.DisableTips.Code, Settings.DisableTips.Name),
        requireFees: getSettingByCodeAndName(Settings.RequireFees.Code, Settings.RequireFees.Name),
        forceEndDate: getSettingByCodeAndName(
            Settings.ForceEndDate.Code,
            Settings.ForceEndDate.Name,
        ),
        hideMoneyRaised: getSettingByCodeAndName(
            Settings.HideMoneyRaised.Code,
            Settings.HideMoneyRaised.Name,
        ),
        hideSupporterFeed: getSettingByCodeAndName(
            Settings.HideSupporterFeed.Code,
            Settings.HideSupporterFeed.Name,
        ),
        hideContactInformation: getSettingByCodeAndName(
            Settings.HideContactInformation.Code,
            Settings.HideContactInformation.Name,
        ),
        disableSpaces: getSettingByCodeAndName(
            Settings.DisableSpaces.Code,
            Settings.DisableSpaces.Name,
        ),
        donationButtonText: getSettingByCodeAndName(
            Settings.DonationButtonText.Code,
            Settings.DonationButtonText.Name,
        ),
        hideOtherAmountField: getSettingByCodeAndName(
            Settings.HideOtherAmountField.Code,
            Settings.HideOtherAmountField.Name,
        ),
        thanksMessage: getSettingByCodeAndName(
            Settings.CampaignSpecificThanksMessage.Code,
            Settings.CampaignSpecificThanksMessage.Name,
        ),
        minimumDonationRequired: getSettingByCodeAndName(
            Settings.MinimumDonationRequired.Code,
            Settings.MinimumDonationRequired.Name,
        ),
        metaTitle: getSettingByCodeAndName(Settings.MetaTitle.Code, Settings.MetaTitle.Name),
        metaDescription: getSettingByCodeAndName(
            Settings.MetaDescription.Code,
            Settings.MetaDescription.Name,
        ),
        themeColor: getSettingByCodeAndName(
            Settings.CampaignSpecificThemeColor.Code,
            Settings.CampaignSpecificThemeColor.Name,
        ),
        maintainAspectRatio: getSettingByCodeAndName(
            Settings.MaintainAspectRatio.Code,
            Settings.MaintainAspectRatio.Name,
        ),
        customDonationSettings: getSettingByCodeAndName(
            Settings.CustomDonations.Code,
            Settings.CustomDonations.Name,
        ),
        minimumDonationSettings: getSettingByCodeAndName(
            Settings.MinimumDonationRequired.Code,
            Settings.MinimumDonationRequired.Name,
        ),
        enableRecurringPayments: getSettingByCodeAndName(
            Settings.EnableRecurringPayments.Code,
            Settings.EnableRecurringPayments.Name,
        ),
        defaultPaymentFrequencySettings: getSettingByCodeAndName(
            Settings.DefaultPaymentFrequency.Code,
            Settings.DefaultPaymentFrequency.Name,
        ),
        enabledPaymentFrequenciesSettings: getSettingByCodeAndName(
            Settings.EnabledPaymentFrequencies.Code,
            Settings.EnabledPaymentFrequencies.Name,
        ),
        fundraiseButtonText: getSettingByCodeAndName(
            Settings.FundraiseButtonText.Code,
            Settings.FundraiseButtonText.Name,
        ),
        disableTeamFundraising: getSettingByCodeAndName(
            Settings.DisableTeamFundraising.Code,
            Settings.DisableTeamFundraising.Name,
        ),
        disableFundraiseButton: getSettingByCodeAndName(
            Settings.DisableFundraiseButton.Code,
            Settings.DisableFundraiseButton.Name,
        ),
        disableCreatingNewTeams: getSettingByCodeAndName(
            Settings.DisableCreatingNewTeams.Code,
            Settings.DisableCreatingNewTeams.Name,
        ),
        hideMemberRaised: getSettingByCodeAndName(
            Settings.HideMemberRaised.Code,
            Settings.HideMemberRaised.Name,
        ),
        enableRegistrationFundraising: getSettingByCodeAndName(
            Settings.EnableRegistrationFundraising.Code,
            Settings.EnableRegistrationFundraising.Name,
        ),
        registrationThankYouMessage: getSettingByCodeAndName(
            Settings.RegistrationThankYouMessage.Code,
            Settings.RegistrationThankYouMessage.Name,
        ),
        enableMultiTeam: getSettingByCodeAndName(
            Settings.EnableMultiTeam.Code,
            Settings.EnableMultiTeam.Name,
        ),
        multiTeamLimit: getSettingByCodeAndName(
            Settings.MultiTeamLimit.Code,
            Settings.MultiTeamLimit.Name,
        ),
        hideTeamRaised: getSettingByCodeAndName(
            Settings.HideTeamRaised.Code,
            Settings.HideTeamRaised.Name,
        ),
        hideFund: getSettingByCodeAndName(Settings.HideFund.Code, Settings.HideFund.Name),
        defaultFund: getSettingByCodeAndName(Settings.DefaultFund.Code, Settings.DefaultFund.Name),
        customDonations: getSettingByCodeAndName(
            Settings.CustomDonations.Code,
            Settings.CustomDonations.Name,
        ),
        disablePayments: getSettingByCodeAndName(
            Settings.DisablePayments.Code,
            Settings.DisablePayments.Name,
        ),
        requirePhoneNumbers: getSettingByCodeAndName(
            Settings.RequirePhoneNumbers.Code,
            Settings.RequirePhoneNumbers.Name,
        ),
        requireBillingAddress: getSettingByCodeAndName(
            Settings.RequireBillingAddress.Code,
            Settings.RequireBillingAddress.Name,
        ),
        defaultPaymentFrequency: getSettingByCodeAndName(
            Settings.DefaultPaymentFrequency.Code,
            Settings.DefaultPaymentFrequency.Name,
        ),
        disableRecurringPayments: getSettingByCodeAndName(
            Settings.DisableRecurringPayments.Code,
            Settings.DisableRecurringPayments.Name,
        ),
        checkPaymentInstructions: getSettingByCodeAndName(
            Settings.CheckPaymentInstructions.Code,
            Settings.CheckPaymentInstructions.Name,
        ),
        enabledPaymentFrequencies: getSettingByCodeAndName(
            Settings.EnabledPaymentFrequencies.Code,
            Settings.EnabledPaymentFrequencies.Name,
        ),
        disablePaymentOnBehalfOfCompany: getSettingByCodeAndName(
            Settings.DisablePaymentOnBehalfOfCompany.Code,
            Settings.DisablePaymentOnBehalfOfCompany.Name,
        ),
    };
};

export type { TransformedCampaign };

export { useCurrentCampaign, useCurrentPossibleCampaign };
